//changed due to package upgrade
import React, { useState } from 'react';
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppTextField from '@projectK/components/AppTextField';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik } from 'formik';
import EditIcon from '@mui/icons-material/Edit';
import AppCard from '@projectK/components/AppCard';
import jwtAxios from '@projectK/services/auth/JWT';
import { useInfoViewActionsContext } from '@projectK/context';
import AppInfoView from '@projectK/components/AppInfoView';
import * as yup from 'yup';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import PropTypes from 'prop-types';

const validationSchema = yup.object({
  desktop_company_calendar_default_view: yup.string().nullable(),
  // .required(<IntlMessages id='validation.companyCalenderViewRequired' />),
  mobile_company_calendar_default_view: yup.string().nullable(),
  // .required(
  //   <IntlMessages id='validation.mobileCompanyCalenderViewRequired' />,
  // ),
  desktop_member_calendar_default_view: yup.string().nullable(),
  // .required(<IntlMessages id='validation.memberCalenderViewRequired' />),
  mobile_member_calendar_default_view: yup.string().nullable(),
  // .required(
  //   <IntlMessages id='validation.mobileMemberCalenderViewRequired' />,
  // ),
  mobile_group_by: yup.string().nullable(),
  // .required(<IntlMessages id='validation.mobileGoupByRequired' />),
  mobile_group_by_orientation: yup.string().nullable(),
  // .required(<IntlMessages id='validation.mobileGoupByOrientationRequired' />),
  desktop_group_by: yup.string().nullable(),
  // .required(<IntlMessages id='validation.desktopGoupByRequired' />),
  desktop_group_by_orientation: yup.string().nullable(),
  // .required(
  //   <IntlMessages id='validation.desktopGoupByOrientationRequired' />,
  // ),
  number_of_events_to_show_on_calendar: yup
    .string()
    .matches(/^[0-9]+$/, 'Please enter a valid number')
    .max(100, 'Number of events must be less than or equal to 100')
    .nullable(),
  // .required(<IntlMessages id='validation.numberOfEventsRequired' />),
});

const Calendar = ({ settings }) => {
  const companyTheme = CompanyTheme();
  const [isEdit, setIsEdit] = useState(false);
  const infoViewActionsContext = useInfoViewActionsContext();

  return (
    <>
      <AppCard>
        <Formik
          enableReinitialize={true}
          validateOnChange={true}
          initialValues={{
            desktop_company_calendar_default_view:
              settings?.desktop_company_calendar_default_view || '',
            mobile_company_calendar_default_view:
              settings?.mobile_company_calendar_default_view || '',
            desktop_member_calendar_default_view:
              settings?.desktop_member_calendar_default_view || '',
            mobile_member_calendar_default_view:
              settings?.mobile_member_calendar_default_view || '',
            mobile_group_by: settings?.mobile_group_by || '',
            mobile_group_by_orientation:
              settings?.mobile_group_by_orientation || '',
            desktop_group_by: settings?.desktop_group_by || '',
            desktop_group_by_orientation:
              settings?.desktop_group_by_orientation || '',
            number_of_events_to_show_on_calendar:
              settings?.number_of_events_to_show_on_calendar || '',
            remember_calendar_filters:
              settings?.remember_calendar_filters === 'true' ? true : false,
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitting(true);
            // Prepare the data for submission

            const settingsArray = Object.keys(values).map((fieldName) => {
              let setting;
              if (values[fieldName] === false || values[fieldName] === true) {
                setting = values[fieldName] + '';
              } else if (values[fieldName] === '') {
                setting = null;
              } else {
                setting = values[fieldName];
              }

              return {
                variant: 'calander', // assuming 'calander' is intentional, should it be 'calendar'?
                slug: fieldName,
                setting,
                is_active: true, // Set is_active as needed
              };
            });

            // Make the PUT request using jwtAxios
            jwtAxios
              .put(`/app-settings/calander/`, settingsArray)
              .then((response) => {
                setSubmitting(false);
                setIsEdit(false);
                infoViewActionsContext.showMessage(
                  'Calendar Settings updated successfully!',
                );
              })
              .catch((error) => {
                // Handle the error here
                infoViewActionsContext.fetchError(error.message);
                console.error('Error updating social settings:', error);
                setSubmitting(false);
              });
          }}
        >
          {({ resetForm, setFieldValue, errors, touched, values }) => (
            <Form noValidate autoComplete='off'>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={{
                      color: companyTheme
                        ? companyTheme.primary.main
                        : 'text.primary',
                    }}
                  >
                    Calendar Settings
                  </Typography>
                </Box>
                <Box>
                  {!isEdit && (
                    <EditIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setIsEdit(true)}
                    />
                  )}
                </Box>
              </Box>
              <Divider sx={{ my: 4 }} />
              <AppGridContainer>
                <Grid size={{ xs: 12, md: 12 }}>
                  <Box
                    sx={{
                      border: '1px solid #EAECF0',
                      p: 6,
                      borderRadius: 3,
                      boxShadow:
                        '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                    }}
                  >
                    <AppGridContainer>
                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(
                                touched.desktop_company_calendar_default_view,
                              ) &&
                              Boolean(
                                errors.desktop_company_calendar_default_view,
                              )
                            }
                          >
                            <InputLabel>
                              Desktop Company Calendar Default View
                            </InputLabel>
                            <Select
                              name='desktop_company_calendar_default_view'
                              label='Desktop Company Calendar Default View'
                              value={
                                values?.desktop_company_calendar_default_view ||
                                ''
                              }
                              onChange={(event) => {
                                setFieldValue(
                                  'desktop_company_calendar_default_view',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Day'>Day</MenuItem>
                              <MenuItem value='Week'>Week</MenuItem>
                              <MenuItem value='Month'>Month</MenuItem>
                              <MenuItem value='Four Week'>Four Week</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.desktop_company_calendar_default_view &&
                                errors.desktop_company_calendar_default_view}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='desktop_company_calendar_default_view'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Desktop Company Calendar Default View'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(
                                touched.mobile_company_calendar_default_view,
                              ) &&
                              Boolean(
                                errors.mobile_company_calendar_default_view,
                              )
                            }
                          >
                            <InputLabel>
                              Mobile Company Calendar Default View
                            </InputLabel>
                            <Select
                              name='mobile_company_calendar_default_view'
                              label='Mobile Company Calendar Default View'
                              value={
                                values?.mobile_company_calendar_default_view
                              }
                              onChange={(event) => {
                                setFieldValue(
                                  'mobile_company_calendar_default_view',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Day'>Day</MenuItem>
                              <MenuItem value='Week'>Week</MenuItem>
                              <MenuItem value='Month'>Month</MenuItem>
                              <MenuItem value='Four Week'>Four Week</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.mobile_company_calendar_default_view &&
                                errors.mobile_company_calendar_default_view}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='mobile_company_calendar_default_view'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Mobile Company Calendar Default View'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(
                                touched.desktop_member_calendar_default_view,
                              ) &&
                              Boolean(
                                errors.desktop_member_calendar_default_view,
                              )
                            }
                          >
                            <InputLabel>
                              Desktop Member Calendar Default View
                            </InputLabel>
                            <Select
                              name='desktop_member_calendar_default_view'
                              label='Desktop Member Calendar Default View'
                              value={
                                values?.desktop_member_calendar_default_view
                              }
                              onChange={(event) => {
                                setFieldValue(
                                  'desktop_member_calendar_default_view',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Day'>Day</MenuItem>
                              <MenuItem value='Week'>Week</MenuItem>
                              <MenuItem value='Month'>Month</MenuItem>
                              <MenuItem value='Four Week'>Four Week</MenuItem>
                              <MenuItem value='Family Week'>
                                Family Week
                              </MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.desktop_member_calendar_default_view &&
                                errors.desktop_member_calendar_default_view}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='desktop_member_calendar_default_view'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Desktop Member Calendar Default View'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(
                                touched.mobile_member_calendar_default_view,
                              ) &&
                              Boolean(
                                errors.mobile_member_calendar_default_view,
                              )
                            }
                          >
                            <InputLabel>
                              Mobile Member Calendar Default View
                            </InputLabel>
                            <Select
                              name='mobile_member_calendar_default_view'
                              label='Mobile Member Calendar Default View'
                              value={
                                values?.mobile_member_calendar_default_view
                              }
                              onChange={(event) => {
                                setFieldValue(
                                  'mobile_member_calendar_default_view',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Day'>Day</MenuItem>
                              <MenuItem value='Week'>Week</MenuItem>
                              <MenuItem value='Month'>Month</MenuItem>
                              <MenuItem value='Four Week'>Four Week</MenuItem>
                              <MenuItem value='Family Week'>
                                Family Week
                              </MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.mobile_member_calendar_default_view &&
                                errors.mobile_member_calendar_default_view}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='mobile_member_calendar_default_view'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Mobile Member Calendar Default View'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(touched.mobile_group_by) &&
                              Boolean(errors.mobile_group_by)
                            }
                          >
                            <InputLabel>Mobile Group By</InputLabel>
                            <Select
                              name='mobile_group_by'
                              label='Mobile Group By'
                              value={values?.mobile_group_by}
                              onChange={(event) => {
                                setFieldValue(
                                  'mobile_group_by',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='None'>None</MenuItem>
                              <MenuItem value='Location'>Location</MenuItem>
                              <MenuItem value='Group'>Group</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.mobile_group_by &&
                                errors.mobile_group_by}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='mobile_group_by'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Mobile Group By'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(touched.mobile_group_by_orientation) &&
                              Boolean(errors.mobile_group_by_orientation)
                            }
                          >
                            <InputLabel>Mobile Group By Orientation</InputLabel>
                            <Select
                              name='mobile_group_by_orientation'
                              label='Mobile Group By Orientation'
                              value={values?.mobile_group_by_orientation}
                              onChange={(event) => {
                                setFieldValue(
                                  'mobile_group_by_orientation',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Vertical'>Vertical</MenuItem>
                              <MenuItem value='Horizontal'>Horizontal</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.mobile_group_by_orientation &&
                                errors.mobile_group_by_orientation}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='mobile_group_by_orientation'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Mobile Group By Orientation'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(touched.desktop_group_by) &&
                              Boolean(errors.desktop_group_by)
                            }
                          >
                            <InputLabel>Desktop Group By</InputLabel>
                            <Select
                              name='desktop_group_by'
                              label='Desktop Group By'
                              value={values?.desktop_group_by}
                              onChange={(event) => {
                                setFieldValue(
                                  'desktop_group_by',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='None'>None</MenuItem>
                              <MenuItem value='Location'>Location</MenuItem>
                              <MenuItem value='Group'>Group</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.desktop_group_by &&
                                errors.desktop_group_by}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='desktop_group_by'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Desktop Group By'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        {isEdit ? (
                          <FormControl
                            sx={{
                              width: '100%',
                            }}
                            error={
                              Boolean(touched.desktop_group_by_orientation) &&
                              Boolean(errors.desktop_group_by_orientation)
                            }
                          >
                            <InputLabel>
                              Desktop Group By Orientation
                            </InputLabel>
                            <Select
                              name='desktop_group_by_orientation'
                              label='Desktop Group By Orientation'
                              value={values?.desktop_group_by_orientation}
                              onChange={(event) => {
                                setFieldValue(
                                  'desktop_group_by_orientation',
                                  event.target.value,
                                );
                              }}
                            >
                              <MenuItem value='Vertical'>Vertical</MenuItem>
                              <MenuItem value='Horizontal'>Horizontal</MenuItem>
                            </Select>
                            <FormHelperText>
                              {touched.desktop_group_by_orientation &&
                                errors.desktop_group_by_orientation}
                            </FormHelperText>
                          </FormControl>
                        ) : (
                          <AppTextField
                            name='desktop_group_by_orientation'
                            variant='outlined'
                            sx={{
                              width: '100%',
                            }}
                            label='Desktop Group By Orientation'
                            slotProps={{
                              input: {
                                readOnly: !isEdit,
                              },
                            }}
                          />
                        )}
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          name='number_of_events_to_show_on_calendar'
                          variant='outlined'
                          value={values.number_of_events_to_show_on_calendar}
                          onChange={(event) => {
                            setFieldValue(
                              'number_of_events_to_show_on_calendar',
                              event.target.value,
                            );
                            touched.number_of_events_to_show_on_calendar = true;
                          }}
                          error={
                            touched.number_of_events_to_show_on_calendar &&
                            Boolean(errors.number_of_events_to_show_on_calendar)
                          }
                          helperText={
                            touched.number_of_events_to_show_on_calendar &&
                            errors.number_of_events_to_show_on_calendar
                          }
                          sx={{
                            width: '100%',
                          }}
                          label='Number of Events to Show on Calendar'
                          slotProps={{
                            input: {
                              readOnly: !isEdit,
                            },
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name='remember_calendar_filters'
                              checked={values.remember_calendar_filters}
                              onChange={(event) => {
                                setFieldValue(
                                  'remember_calendar_filters',
                                  event.target.checked,
                                );
                              }}
                              disabled={!isEdit}
                            />
                          }
                          label='Remember Calendar Filters'
                        />
                      </Grid>
                    </AppGridContainer>
                  </Box>
                </Grid>
              </AppGridContainer>
              {isEdit && (
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  spacing={5}
                  sx={{ mt: 3 }}
                >
                  <Button
                    sx={{
                      borderColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        borderColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8)
                          : alpha('#2196f3', 0.8),
                      },
                      color: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                    }}
                    onClick={() => {
                      setIsEdit(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    color='primary'
                    type='submit'
                    sx={{
                      backgroundColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        backgroundColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                          : alpha('#2196f3', 0.8),
                      },
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              )}
            </Form>
          )}
        </Formik>
      </AppCard>
      <AppInfoView />
    </>
  );
};
Calendar.propTypes = {
  settings: PropTypes.object,
};
export default Calendar;
